import { useState, useCallback, useRef, useEffect } from "react";
import { searchPosts, searchUsers, getExplorePosts } from "../APIs/searchApi";

const useSearch = () => {
  const [query, setQuery] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  const pageRef = useRef(1);
  const loadingRef = useRef(false);
  const debounceRef = useRef(null);

  // Explore load
  const loadExplore = useCallback(async (reset = false) => {
    if (loadingRef.current && !reset) return;
    loadingRef.current = true;
    reset ? setLoading(true) : setLoadingMore(true);

    try {
      const { posts: data, next } = await getExplorePosts(pageRef.current);
      setPosts((prev) => reset ? data : [...prev, ...data]);
      setHasMore(!!next);
      if (!reset) pageRef.current += 1;
    } catch (err) {
      setError("Could not load explore.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingRef.current = false;
    }
  }, []);

  // Search posts + users
  const handleSearch = useCallback(async (q, reset = false) => {
    if (!q.trim()) { loadExplore(true); return; }
    if (loadingRef.current && !reset) return;
    loadingRef.current = true;
    reset ? setLoading(true) : setLoadingMore(true);

    try {
      // Posts aur users parallel fetch karo
      const [{ posts: postData, next }, userList] = await Promise.all([
        searchPosts(q, reset ? 1 : pageRef.current),
        reset ? searchUsers(q) : Promise.resolve(null),
      ]);

      setPosts((prev) => reset ? postData : [...prev, ...postData]);
      if (userList !== null) setUsers(userList);
      setHasMore(!!next);
      if (!reset) pageRef.current += 1;
      else pageRef.current = 1;
    } catch (err) {
      setError("Search failed.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingRef.current = false;
    }
  }, [loadExplore]);

  // Debounced query change
  const handleQueryChange = useCallback((value) => {
    setQuery(value);
    setIsTyping(value.length > 0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      pageRef.current = 1;
      if (value.trim()) {
        handleSearch(value, true);
      } else {
        setIsTyping(false);
        setUsers([]);
        loadExplore(true);
      }
    }, 500);
  }, [handleSearch, loadExplore]);

  // Load more
  const loadMore = useCallback(() => {
    if (!hasMore || loadingRef.current) return;
    pageRef.current += 1;
    query.trim() ? handleSearch(query, false) : loadExplore(false);
  }, [hasMore, query, handleSearch, loadExplore]);

  // Initial load
  useEffect(() => { loadExplore(true); }, [loadExplore]);

  return {
    query, isTyping, posts, users,
    loading, loadingMore, hasMore, error,
    handleQueryChange, loadMore, setIsTyping,
  };
};

export default useSearch;